import useInterval from '@hooks/useInterval';
import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { formatTime } from './utils';

const initialTime = 5 * 60;

export const useTimer = () => {
  const [time, setTime] = useState(initialTime);
  const [live, setLive] = useState(true);
  const [timerStart, setTimerStart] = useState(Date.now());

  useInterval(() => {
    if (live) {
      if (time <= 0) {
        stopTimer();
      } else {
        setTime((curr) => curr - 1);
      }
    }
  }, 1000);

  const stopTimer = () => {
    setTime(0);
    setLive(false);
    setTimerStart(0);
  };

  const startTimer = () => {
    setTime(initialTime);
    setLive(true);
    setTimerStart(Date.now());
  };

  useEffect(() => {
    if (!document.hidden && time > 0) {
      const timeElapsed = Math.floor((Date.now() - timerStart) / 1000);
      const syncedTime = initialTime - timeElapsed;
      setTime(Math.max(syncedTime, 0));
    }
  }, [document.hidden]);

  return {
    timeRemaining: formatTime(time),
    live,
    setLive,
    startTimer,
    stopTimer,
  };
};

export const useFilters = () => {
  const [searchText, setSearchText] = useState('');
  const [sourceFilter, setSourceFilter] = useState([] as string[]);
  const [destinationFilter, setDestinationFilter] = useState([] as string[]);

  const updateSearchText = (input: React.FormEvent<HTMLInputElement>) => {
    setSearchText(input.currentTarget.value);
  };

  const onResetFilter = () => {
    setSearchText('');
    setSourceFilter([]);
    setDestinationFilter([]);
  };

  return {
    searchText,
    sourceFilter,
    destinationFilter,
    setDestinationFilter,
    updateSearchText,
    onResetFilter,
    onSourceFilterChange: setSourceFilter,
    onDestinationFilterChange: setDestinationFilter,
  };
};

export const useOpenLiveEvents = (
  orgStore: { isFreeTrial: boolean; isPro: boolean },
  baseUrl: string,
  id: string,
) => {
  const history = useHistory();
  let url = `/${baseUrl}/live/intro/${id}`;
  if (orgStore.isFreeTrial || orgStore.isPro) {
    url = `/${baseUrl}/live/${id}`;
  }
  return {
    onClick: () => history.push(url),
  };
};

export const useLiveEvents = <T extends { id: number }>() => {
  const [selected, setSelected] = useState<T | undefined>(undefined);
  const [events, setEvents] = useState<T[]>([]);
  const [latestId, setLatestId] = useState(0);
  const [fetchFrom, setFetchFrom] = useState<number>(moment().valueOf());

  const onClear = () => {
    setEvents([]);
    setSelected(undefined);
    setFetchFrom(moment().valueOf());
  };

  const addNewEvents = (newEvents: T[]) => {
    if (newEvents.length > 0) {
      setEvents(events.concat([...newEvents].reverse()));
      setLatestId(newEvents[0].id);
    }
  };

  return {
    selected,
    setSelected,
    events,
    onClear,
    latestId,
    addNewEvents,
    fetchFrom,
  };
};
